/**
 * web/src/lib/workflow-step-defs.ts
 * Canonical step list per workflow type. Mirrors the backend definitions
 * (src/pixel/workflows/sponsor/definition.py + speaker/definition.py — the
 * engine source of truth) so the Workflow Detail timeline can render all
 * steps — including future ones the engine hasn't created StepExecution
 * rows for yet — instead of only the steps that have already started.
 *
 * Keep this file in sync with the engine definitions when step counts or
 * names change. The fields here cover only what the timeline needs to
 * render (id / display name / type / role); a synthetic StepExecution
 * is built per missing step in `padStepsToDefinition()`. Rich speaker
 * journey copy (trigger chips, summaries, fires tags) comes from the
 * journey API, not this file.
 */
import type { StepExecution, Role, StepType } from '../api/types';

export interface StepDef {
  id: string;
  name: string;
  type: StepType;
  gateRole?: Role;
}

const SPONSOR_ONBOARDING_STEPS: StepDef[] = [
  { id: 'trigger_event', name: 'Trigger Event', type: 'automated' },
  { id: 'contract_saved', name: 'Contract Saved', type: 'automated' },
  { id: 'contract_details_to_tracker', name: 'Contract Details To Tracker', type: 'automated' },
  {
    id: 'supplementary_review',
    name: 'Supplementary Data Review',
    type: 'human_gate',
    gateRole: 'sales_ops',
  },
  { id: 'logo_check', name: 'Logo Check', type: 'automated' },
  { id: 'reg_code', name: 'Registration Code', type: 'human_gate', gateRole: 'sales_ops' },
  { id: 'welcome_email', name: 'Welcome Email', type: 'human_gate', gateRole: 'sales_ops' },
  { id: 'welcome_receipt', name: 'Welcome Receipt Tracking', type: 'automated' },
  {
    id: 'ops_next_steps',
    name: 'Ops Next Steps Email',
    type: 'human_gate',
    gateRole: 'operations',
  },
  { id: 'ops_receipt', name: 'Ops Receipt Tracking', type: 'automated' },
];

// Speaker onboarding v2 — ONE banded workflow, 10 steps (must match the
// backend ids/order/types in src/pixel/workflows/speaker/definition.py):
// band A steps 0-3 sequential, band B steps 4-9 armed all at once.
const SPEAKER_ONBOARDING_STEPS: StepDef[] = [
  { id: 'invite', name: 'Invitation', type: 'human_gate', gateRole: 'content' },
  { id: 'invite_reply', name: 'Awaiting Reply', type: 'automated' },
  {
    id: 'confirmation',
    name: 'Confirmation & Handoff',
    type: 'human_gate',
    gateRole: 'content',
  },
  { id: 'asset_collection', name: 'Asset Collection & Tracking', type: 'automated' },
  { id: 'prep_call', name: 'Prep Call', type: 'automated' },
  { id: 'prep_notes', name: 'Prep Call Notes', type: 'human_gate', gateRole: 'content' },
  { id: 'card_assets', name: 'Generate Illustrations', type: 'human_gate', gateRole: 'content' },
  { id: 'promotion', name: 'Speaker Promotion', type: 'human_gate', gateRole: 'content' },
  { id: 'logistics', name: 'Final Logistics', type: 'human_gate', gateRole: 'content' },
  { id: 'thank_you', name: 'Post-Event Thank-You', type: 'human_gate', gateRole: 'content' },
];

const TEST_WORKFLOW_STEPS: StepDef[] = [
  { id: 'first_auto', name: 'First Auto', type: 'automated' },
  { id: 'human_gate', name: 'Human Gate', type: 'human_gate' },
  { id: 'second_auto', name: 'Second Auto', type: 'automated' },
  { id: 'final_step', name: 'Final Step', type: 'automated' },
];

/**
 * Returns the canonical step list for a given workflow type. Returns an
 * empty array for unknown types — the caller falls back to whatever the
 * server returned.
 */
export function getStepDefs(workflowType: string): StepDef[] {
  switch (workflowType) {
    case 'sponsor_onboarding':
      return SPONSOR_ONBOARDING_STEPS;
    case 'speaker_onboarding':
      return SPEAKER_ONBOARDING_STEPS;
    case 'test_workflow':
      return TEST_WORKFLOW_STEPS;
    default:
      return [];
  }
}

/**
 * Total step count for a workflow type, for "Step N of M" displays and
 * progress bars. Single source of truth — derived from `getStepDefs` so it
 * can never drift from the timeline. Falls back to 1 for unknown types
 * (avoids a divide-by-zero / empty progress bar).
 */
export function getStepCount(workflowType: string): number {
  return getStepDefs(workflowType).length || 1;
}

/**
 * Human-readable label for a workflow type (list section headers, etc.).
 * Returns the raw type string for unknown types so nothing renders blank.
 */
export function workflowTypeLabel(workflowType: string): string {
  switch (workflowType) {
    case 'sponsor_onboarding':
      return 'Sponsor Onboarding';
    case 'speaker_onboarding':
      return 'Speaker Onboarding';
    case 'test_workflow':
      return 'Test Workflows';
    default:
      return workflowType;
  }
}

/**
 * The list tab a workflow "belongs to" since the 2026-07 nav restructure
 * (no generic Workflows tab): speaker onboarding lives under the Speakers
 * roster/timeline; everything else (sponsor + test) under Sponsors. Used
 * for breadcrumbs + back-navigation on the shared detail/gate pages.
 */
export function workflowHomeCrumb(workflowType: string | undefined): { label: string; to: string } {
  return workflowType === 'speaker_onboarding'
    ? { label: 'Speakers', to: '/speakers' }
    : { label: 'Sponsors', to: '/sponsors' };
}

/**
 * Pads the server-returned StepExecution[] with synthetic placeholders
 * for steps the engine hasn't created rows for yet. Real rows always
 * take precedence; placeholders only fill positions where no real row
 * exists at that stepIndex.
 *
 * The synthetic StepExecution carries status='pending' so StepCard's
 * variant logic renders it as a future row.
 *
 * @param real the StepExecution rows from /api/workflows/:id (server
 *             orders by stepIndex asc)
 * @param workflowId parent workflow id (mirrored into placeholders)
 * @param defs canonical step definitions for this workflow type
 */
export function padStepsToDefinition(
  real: StepExecution[],
  workflowId: string,
  defs: StepDef[],
): StepExecution[] {
  if (defs.length === 0) return real;
  const byIndex = new Map<number, StepExecution>();
  for (const s of real) byIndex.set(s.stepIndex, s);
  const out: StepExecution[] = [];
  for (let i = 0; i < defs.length; i++) {
    const real = byIndex.get(i);
    if (real) {
      out.push(real);
    } else {
      out.push(makePlaceholder(workflowId, i, defs[i]));
    }
  }
  return out;
}

/**
 * Builds a synthetic StepExecution row for a step the engine hasn't
 * created yet. Status is 'pending'; all temporal/output/error fields are
 * null. Field set mirrors web/src/api/types.ts StepExecution exactly so
 * downstream consumers don't need to handle a partial shape.
 */
function makePlaceholder(workflowId: string, stepIndex: number, def: StepDef): StepExecution {
  const now = new Date().toISOString();
  return {
    id: `placeholder:${workflowId}:${stepIndex}`,
    workflowId,
    stepIndex,
    stepId: def.id,
    stepName: def.name,
    stepType: def.type,
    status: 'pending',
    gateRole: def.gateRole ?? null,
    gateSchema: null,
    gateData: null,
    approvedBy: null,
    approvedByName: null,
    approvedAt: null,
    approverEdits: null,
    rejectionReason: null,
    output: null,
    errorMessage: null,
    retryCount: 0,
    progress: {},
    scheduledFor: null,
    sendWindowStart: null,
    sendWindowEnd: null,
    startedAt: null,
    completedAt: null,
    durationMs: null,
    createdAt: now,
    updatedAt: now,
  };
}
